import { pageMm } from './printPaper'
import type { PaperFormat, PrintOrientation } from './printPaper'
import { CUSTOM_PRINT_SCALES, PRINT_SCALES, groundExtentMetres, SERVER_MIN_SCALE, SERVER_MAX_SCALE, SERVER_MAX_PAGE_MM } from './printScale'

// Re-export for backward compatibility
export { SERVER_MIN_SCALE, SERVER_MAX_SCALE, SERVER_MAX_PAGE_MM }

/**
 * The most ground a single sheet may span, per dimension, in kilometres.
 *
 * This is a projection limit, not a cost limit. A printout is rendered in one
 * projection, chosen from where the sheet's CENTRE falls, and every projection
 * is only true near its own reference — so a sheet much wider than this carries
 * edges whose ground is no longer where the paper says it is.
 *
 * The reference differs by projection, which is why the cap is stated in ground
 * kilometres rather than in anything projection-specific. TM35FIN and SWEREF99
 * TM are transverse Mercators, true along a central meridian and drifting
 * east-west from it — an EXTENDED zone in Finland's case, so "UTM zone width"
 * was never quite the rule even before the fallback. Lambert-93 is a conic,
 * true along two standard parallels and drifting north-south instead. The
 * worldwide Mercator fallback drifts north-south too, and fastest of the three:
 * its scale runs as 1/cos(latitude), which over 100 km of latitude is about 3%
 * edge to edge at 60°N and 4% at 70°N. That is the error this number is chosen
 * to bound, and it is why 500 km would not do.
 *
 * BOTH edges are capped in every case, because NEITHER EDGE HAS A COMPASS
 * DIRECTION until the sheet is oriented. Portrait and landscape swap the two,
 * and a custom sheet states its millimetres in whatever order the user typed
 * them; a rule that capped only the nominal width would be defeated by turning
 * the paper, and would make the same print legal in one orientation and refused
 * in the other. It also keeps a very tall sheet from running far out of the
 * region whose projection was chosen from the sheet's centre, which is the same
 * failure by a different route.
 *
 * It bounds render cost as a side effect, which is why no separate cost cap
 * exists. `scale <= 100 000 000 / printable_mm`, so a 2000 mm sheet cannot go
 * coarser than 1:50 505 — and 1:50 000 is the measured cost peak (483 s and
 * 596 MB at 2000 x 2000 mm, against a 4-hour server timeout). The most
 * expensive job the UI can express is therefore the boundary case, and it is
 * comfortably affordable.
 */
export const MAX_GROUND_DIMENSION_KM = 100

/** Why a print cannot be submitted, or null when it can. */
export type MapPrintLimitBreach = 'scale' | 'page' | 'extent'

export interface MapPrintLimitCheck {
  scale: number
  /** The sheet, either as a named format or as explicit millimetres. */
  format?: PaperFormat
  orientation?: PrintOrientation
  page?: { w: number; h: number }
}

export function checkMapPrintLimits({
  scale,
  format,
  orientation = 'portrait',
  page,
}: MapPrintLimitCheck): MapPrintLimitBreach | null {
  if (!Number.isFinite(scale) || scale < SERVER_MIN_SCALE || scale > SERVER_MAX_SCALE) {
    return 'scale'
  }

  const sheet = page ?? (format ? pageMm(format, orientation) : null)
  if (sheet === null) return null

  // Both edges, not the larger one: the server bounds each dimension separately,
  // so a long thin sheet can breach on its height while its width looks fine.
  if (sheet.w > SERVER_MAX_PAGE_MM || sheet.h > SERVER_MAX_PAGE_MM) return 'page'

  // Checked last, so a request that breaks a hard server limit is reported as
  // such: our own cap is a choice we could revisit, and saying "too much ground"
  // about a scale the server flatly cannot render would misdirect the reader.
  //
  // `sheet` is already resolved above (an explicit `page` wins over
  // `format` + `orientation`), so it is passed straight back in as `page` here
  // rather than re-derived. `format`/`orientation` are optional on
  // `PrintSheetSpec` for exactly this case: once `page` is set they are never
  // consulted, so there is no format to invent here any more.
  const ground = groundExtentMetres({ scale, format, orientation, page: sheet })
  const longestKm = Math.max(ground.w, ground.h) / 1000
  if (longestKm > MAX_GROUND_DIMENSION_KM) return 'extent'

  return null
}

/**
 * Whether this paper-and-scale pairing is offered at all.
 *
 * Used to disable the scale buttons a chosen sheet cannot sensibly use, so the
 * limit is visible in the picker rather than discovered after pressing print.
 */
export function isCombinationOffered(combination: MapPrintLimitCheck): boolean {
  return checkMapPrintLimits(combination) === null
}

/**
 * The requested scale if this sheet allows it, otherwise the coarsest one it does.
 *
 * Needed because paper and scale are chosen independently: picking a bigger sheet
 * can withdraw the scale already selected. Without this, the panel would sit with a
 * disabled scale selected and a disabled print button — technically honest, but a
 * dead end the user has to work out for themselves. Falling back to the coarsest
 * legal scale keeps the intent (they wanted to see a lot of ground) while the
 * coverage line immediately shows what they will actually get.
 */
export function clampScaleToOffered(
  scale: number,
  format: PaperFormat,
  orientation: PrintOrientation,
  // Explicit millimetres win over the named format, exactly as they do in
  // `checkMapPrintLimits`. Without it a custom sheet would be clamped as
  // whatever `paperSize` falls back to — A4 — so a 2000 mm sheet would keep a
  // scale that breaches the ground cap and a small one would lose scales it
  // could actually render.
  page?: { w: number; h: number },
): number {
  if (isCombinationOffered({ scale, format, orientation, page })) return scale

  const offered = PRINT_SCALES.filter((candidate) =>
    isCombinationOffered({ scale: candidate, format, orientation, page }),
  )
  // PRINT_SCALES runs finest to coarsest, so the last survivor is the coarsest.
  return offered.length > 0 ? offered[offered.length - 1] : PRINT_SCALES[0]
}

/**
 * The requested custom scale if this sheet allows it, otherwise the coarsest
 * one from the custom series that it does.
 *
 * For the scale the APP derives from the viewport, not the one the user typed.
 * A fixed scale is picked deliberately and a breach is worth reporting; a
 * derived one is written by the panel itself as the hunter zooms, so letting it
 * run past the ground cap would disable Create PDF from a gesture made on the
 * map rather than on the panel — a dead button with no control next to it to
 * explain itself. Capping instead means the frame stops growing with the view,
 * which is visible on screen and is the honest way to say "this is as much
 * ground as one projection zone carries".
 *
 * Unchanged when NO candidate is legal, because then the breach is the page
 * rather than the scale (a sheet past the server's own millimetre limit), and
 * quietly rescaling would hide a problem no scale can fix.
 */
export function clampCustomScaleToSheet(
  scale: number,
  sheet: Omit<MapPrintLimitCheck, 'scale'>,
): number {
  if (checkMapPrintLimits({ ...sheet, scale }) === null) return scale

  // Coarsest first: ground grows with the scale, so the legal scales are the
  // finest ones and the first survivor walking down is the coarsest of them.
  for (let i = CUSTOM_PRINT_SCALES.length - 1; i >= 0; i -= 1) {
    const candidate = CUSTOM_PRINT_SCALES[i]
    if (candidate < scale && checkMapPrintLimits({ ...sheet, scale: candidate }) === null) {
      return candidate
    }
  }
  return scale
}
